'use client';

import Image from 'next/image';
import Link from 'next/link';

import { Mail, MapPin, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { LandingPageFooterProps } from '../../_lib/landing-page-layout-registry';

import '../styles.css';

const FOOTER_LOGO = '/landing-pages/delta/images/logo-full.png';

// Contact facts are tenant data (sourced from nadena-nc.com), not UI copy —
// keep them concrete. Only the labels around them are translated.
const CONTACT = {
  addressEn: 'Makkah Al Mukarramah Rd, Al Wizarat, Riyadh 12622',
  addressAr: 'طريق مكة المكرمة، حي الوزارات، الرياض 12622',
  email: 'info@nadena-nc.com',
  phone: '800 111 1067',
  phoneTel: '8001111067',
};

// Quick links mirror the source footer's nav.
const quickLinks = [
  { labelKey: 'landing:nav.home', fallback: 'Home', href: '/' },
  { labelKey: 'landing:nav.about', fallback: 'About', href: '/#about' },
  { labelKey: 'landing:nav.activities', fallback: 'Activities', href: '/#activities' },
  { labelKey: 'landing:nav.contact', fallback: 'Contact', href: '/contact' },
];

const legalLinks = [
  { labelKey: 'landing:footer.termsOfService', fallback: 'Terms & Conditions', href: '/terms-of-service' },
  { labelKey: 'landing:footer.privacyPolicy', fallback: 'Privacy Policy', href: '/privacy-policy' },
];

// Social channels from the source footer.
const socials = [
  { label: 'Instagram', href: 'https://www.instagram.com/nadena_nc/' },
  { label: 'Snapchat', href: 'https://snapchat.com/t/fbENuteZ' },
  { label: 'X', href: 'https://x.com/Nadena_nc' },
  { label: 'TikTok', href: 'https://www.tiktok.com/@nadena_nc' },
  { label: 'YouTube', href: 'https://www.youtube.com/@nadena_nc' },
];

export function DeltaFooter({ logoUrl }: LandingPageFooterProps) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');
  const logo = logoUrl || FOOTER_LOGO;
  const address = isArabic ? CONTACT.addressAr : CONTACT.addressEn;

  return (
    <footer className="delta-landing bg-[var(--delta-ink)] py-16 text-white lg:py-20">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-10">
          {/* Brand + address */}
          <div className="lg:col-span-5">
            <Image
              src={logo}
              alt="Nadena"
              width={1324}
              height={561}
              className="h-12 w-auto"
            />
            <ul className="mt-6 space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[var(--delta-green)]" />
                <span>{address}</span>
              </li>
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="flex items-center gap-3 transition-colors hover:text-white"
                >
                  <Mail className="h-4 w-4 shrink-0 text-[var(--delta-green)]" />
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${CONTACT.phoneTel}`}
                  dir="ltr"
                  className="flex items-center gap-3 transition-colors hover:text-white"
                >
                  <Phone className="h-4 w-4 shrink-0 text-[var(--delta-green)]" />
                  {CONTACT.phone}
                </a>
              </li>
            </ul>
          </div>

          {/* Quick links */}
          <div className="lg:col-span-3">
            <p className="text-xs font-semibold tracking-[2px] text-white/90 uppercase">
              {t('landing:footer.quickLinks', { defaultValue: 'Quick Links' })}
            </p>
            <ul className="mt-4 space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.href + link.labelKey}>
                  <Link
                    href={link.href}
                    className="text-sm text-white/70 transition-colors hover:text-[var(--delta-green)]"
                  >
                    {t(link.labelKey, { defaultValue: link.fallback })}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Follow us */}
          <div className="lg:col-span-4">
            <p className="text-xs font-semibold tracking-[2px] text-white/90 uppercase">
              {t('landing:footer.followUs', { defaultValue: 'Follow Us' })}
            </p>
            <ul className="mt-4 space-y-2.5">
              {socials.map((social) => (
                <li key={social.href}>
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white/70 transition-colors hover:text-[var(--delta-green)]"
                  >
                    {social.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider + copyright */}
        <div className="mt-14 border-t border-white/15 pt-6">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-[11px] tracking-wide text-white/55">
              © {new Date().getFullYear()}{' '}
              {t('landing:footer.copyright', {
                defaultValue: 'Nadena. All rights reserved.',
              })}
            </p>

            <nav aria-label="Legal" className="flex items-center gap-6">
              {legalLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-[11px] tracking-wide text-white/55 transition-colors hover:text-white"
                >
                  {t(link.labelKey, { defaultValue: link.fallback })}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
}
